import {FormSectionHeading} from '@sollapay/ui/components';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {useAiFillFlash, useFieldAiProps} from '@/features/ai-extraction';
import {AssetDescriptorFields} from '@/features/buyers/components';
import {BLANK_ASSET_FIELDS} from '@/features/buyers/validation';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {PurchasePriceField} from './PurchasePriceField';

import type {AiSuggestion} from '@/features/ai-extraction';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {AssetType} from '@sollapay/enums';
import type {FC} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface AssetDetailsSectionProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  markFieldModified: (field: string) => void;
}

export const AssetDetailsSection: FC<AssetDetailsSectionProps> = ({
  form,
  aiFields,
  isProcessing,
  markFieldModified
}) => {
  const {t} = useTranslation('trustAccounts');
  const strategy = useTrustTypeStrategy();
  const {
    control,
    register,
    setValue,
    getValues,
    formState: {errors}
  } = form;
  const prefix = 'view.buyers.addBuyerModal.assetDetails';
  const setDebugValue = setValue as DebugSetValue;

  const fieldAi = useFieldAiProps({
    aiFields,
    isProcessing,
    markFieldModified,
    getValue: getValues
  });
  const isFlashing = useAiFillFlash(aiFields);

  const assetType = useWatch({control, name: 'asset.assetType'});
  const allowedAssetTypes = strategy.getAllowedAssetTypes();
  const hasAssetTypeChoice = allowedAssetTypes.length > 1;

  const handleAssetTypeChange = (next: AssetType) => {
    setValue('asset', {
      assetType: next,
      ...BLANK_ASSET_FIELDS,
      attachments: getValues('asset.attachments')
    });
  };

  const purchasePriceField = (
    <PurchasePriceField
      form={form}
      fieldAi={fieldAi}
      isFlashing={isFlashing}
      className={hasAssetTypeChoice ? undefined : 'col-span-2'}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <FormSectionHeading title={t(`${prefix}.title`)} />

      <AssetDescriptorFields
        control={control}
        register={register}
        errors={errors}
        namePrefix="asset"
        assetType={assetType}
        allowedAssetTypes={allowedAssetTypes}
        onAssetTypeChange={handleAssetTypeChange}
        i18nPrefix={prefix}
        ai={{fieldAi, isFlashing}}
        debug={{setDebugValue}}
        trailingField={purchasePriceField}
      />
    </div>
  );
};
